import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Download, Pencil, Check, X as XIcon, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import { useVendors, usePlants, useErpSystems } from "../../lib/useReference";
import { useToast } from "../../lib/toast";
import { formatDate } from "../../lib/dateFormat";
import { useDebounce } from "../../lib/useDebounce";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { FieldSelect, FieldInput, DateFormatRadios } from "../../components/ui/Field";
import { Drawer } from "../../components/ui/Drawer";
import { LineStatusBadge } from "../../components/ui/StatusBadge";
import { SkeletonRows } from "../../components/ui/Skeleton";

interface SlaLine {
  id: number;
  releaseNumber: string;
  slNumber: string;
  slaNumber: string;
  slaLineItem: number;
  materialNumber: string;
  materialDescription: string;
  deliveryDate: string;
  originalQty: number;
  unit: string;
  plantId: number;
  unitPrice: number | null;
  currency: string;
  vendorId: number;
  paymentTerms: string | null;
  mrpControllerNumber: string | null;
  mrpControllerName: string | null;
  regionalBuyer: string | null;
  regionalBuyerName: string | null;
  purchaseOrderGroup: string | null;
  status: "unconfirmed" | "confirmed";
  createdAt: string;
}

interface Comment {
  id: number;
  comment: string;
  createdAt: string;
  createdByEmail: string | null;
}

export function SlaResult() {
  const [vendorId, setVendorId] = useState("");
  const [plantId, setPlantId] = useState("");
  const [materialNumber, setMaterialNumber] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [erpId, setErpId] = useState("");
  const [dateFormat, setDateFormat] = useState("DD/MMM/YYYY");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [detail, setDetail] = useState<SlaLine | null>(null);
  const { data: vendors } = useVendors();
  const { data: plants } = usePlants();
  const { data: erpSystems } = useErpSystems();
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const vendorMap = useMemo(() => new Map((vendors ?? []).map((v) => [v.id, v.vendorNumber])), [vendors]);
  const plantMap = useMemo(() => new Map((plants ?? []).map((p) => [p.id, p.plantNumber])), [plants]);

  const materialNumberDebounced = useDebounce(materialNumber);

  const query = useQuery({
    queryKey: ["sla-lines", vendorId, plantId, materialNumberDebounced, fromDate, toDate, erpId],
    queryFn: () =>
      api.get<{ rows: SlaLine[]; total: number }>(
        `/sla/lines?${new URLSearchParams({
          ...(vendorId && { vendorId }),
          ...(plantId && { plantId }),
          ...(materialNumberDebounced && { materialNumber: materialNumberDebounced }),
          ...(fromDate && { fromDate }),
          ...(toDate && { toDate }),
          ...(erpId && { erpId }),
        })}`
      ),
  });

  const commentsQuery = useQuery({
    enabled: !!detail,
    queryKey: ["sla-line-detail", detail?.id],
    queryFn: () => api.get<{ comments: Comment[] }>(`/sla/lines/${detail!.id}`),
  });

  const confirmMutation = useMutation({
    mutationFn: (slaLineIds: number[]) => api.post<{ results: Array<{ ok: boolean }> }>("/sla/lines/confirm", { slaLineIds }),
    onSuccess: (res) => {
      toast(`Confirmed ${res.results.filter((r) => r.ok).length} of ${res.results.length} SLAs`);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["sla-lines"] });
    },
    onError: () => toast("Confirmation failed", "error"),
  });

  const rows = query.data?.rows ?? [];
  const total = query.data?.total ?? rows.length;

  const activeFilters = useMemo(
    () =>
      [
        vendorId && { key: "vendorId", label: `Vendor: ${vendorMap.get(Number(vendorId)) ?? vendorId}`, clear: () => setVendorId("") },
        plantId && { key: "plantId", label: `Plant: ${plantMap.get(Number(plantId)) ?? plantId}`, clear: () => setPlantId("") },
        materialNumber && { key: "materialNumber", label: `Material: ${materialNumber}`, clear: () => setMaterialNumber("") },
        fromDate && { key: "fromDate", label: `From: ${fromDate}`, clear: () => setFromDate("") },
        toDate && { key: "toDate", label: `To: ${toDate}`, clear: () => setToDate("") },
        erpId && { key: "erpId", label: `ERP: ${erpId}`, clear: () => setErpId("") },
      ].filter(Boolean) as Array<{ key: string; label: string; clear: () => void }>,
    [vendorId, plantId, materialNumber, fromDate, toDate, erpId, vendorMap, plantMap]
  );

  function changeSelected() {
    if (!selected.size) return toast("Select at least one SLA line first", "error");
    sessionStorage.setItem("sla-split-ids", JSON.stringify([...selected]));
    sessionStorage.setItem("sla-split-source", "confirmation");
    navigate("/sla-split");
  }

  return (
    <div>
      <Card className="mb-5">
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          <FieldSelect label="Vendor Number" value={vendorId} onChange={(e) => setVendorId(e.target.value)} options={[{ value: "", label: "All vendors" }, ...(vendors ?? []).map((v) => ({ value: v.id, label: `${v.vendorNumber} - ${v.name}` }))]} />
          <FieldSelect label="Plant Number" value={plantId} onChange={(e) => setPlantId(e.target.value)} options={[{ value: "", label: "All plants" }, ...(plants ?? []).map((p) => ({ value: p.id, label: `${p.plantNumber} - ${p.name}` }))]} />
          <FieldInput label="Material Number" value={materialNumber} onChange={(e) => setMaterialNumber(e.target.value)} />
          <FieldSelect label="ERPID" value={erpId} onChange={(e) => setErpId(e.target.value)} options={[{ value: "", label: "All ERP systems" }, ...(erpSystems ?? []).map((e) => ({ value: e.name, label: e.name }))]} />
          <div>
            <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">SLA's Created Range</label>
            <div className="mt-1 flex gap-2">
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="text-sm border border-slate-200 dark:border-white/10 rounded-lg px-2 py-2 bg-white dark:bg-white/5 w-1/2" />
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="text-sm border border-slate-200 dark:border-white/10 rounded-lg px-2 py-2 bg-white dark:bg-white/5 w-1/2" />
            </div>
          </div>
        </div>
        {activeFilters.length > 0 && (
          <div className="px-5 pb-5 flex flex-wrap gap-2">
            {activeFilters.map((f) => (
              <button key={f.key} onClick={f.clear} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/20">
                {f.label}
                <XIcon className="w-3 h-3" />
              </button>
            ))}
          </div>
        )}
      </Card>
      <Card>
        <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-100 dark:border-white/10">
          <span className="text-xs text-slate-400 shrink-0">
            {rows.length} of {total} unconfirmed lines
          </span>
          <div className="shrink-0">
            <DateFormatRadios value={dateFormat} onChange={setDateFormat} />
          </div>
          <div className="ml-auto">
            <button
              onClick={() => query.refetch()}
              disabled={query.isFetching}
              title="Refresh"
              aria-label="Refresh"
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 outline-none focus-visible:ring-2 focus-visible:ring-sbd-yellow/60 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${query.isFetching ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="data w-full text-xs">
            <thead className="bg-slate-50 dark:bg-white/5 text-slate-500">
              <tr>
                <th className="p-3 text-left">
                  <input type="checkbox" checked={rows.length > 0 && selected.size === rows.length} onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} />
                </th>
                <th className="p-3 text-left">SLA/SL Release Creation</th>
                <th className="p-3 text-left">Release Number</th>
                <th className="p-3 text-left">SL Number</th>
                <th className="p-3 text-left">SLA Number</th>
                <th className="p-3 text-left">SLA Line</th>
                <th className="p-3 text-left">SLA Line Status</th>
                <th className="p-3 text-left">Material</th>
                <th className="p-3 text-left">Description</th>
                <th className="p-3 text-left">Original Delivery Date</th>
                <th className="p-3 text-left">Proposed Delivery Date</th>
                <th className="p-3 text-right">Original Qty</th>
                <th className="p-3 text-right">Proposed Qty</th>
                <th className="p-3 text-left">Unit</th>
                <th className="p-3 text-left">Plant Number</th>
                <th className="p-3 text-right">Unit Price</th>
                <th className="p-3 text-left">Currency</th>
                <th className="p-3 text-left">Vendor Number</th>
                <th className="p-3 text-left">Payment Terms</th>
                <th className="p-3 text-left">MRP Cont.</th>
                <th className="p-3 text-left">MRP Cont. Name</th>
                <th className="p-3 text-left">Regional Buyer</th>
                <th className="p-3 text-left">Regional Buyer Name</th>
                <th className="p-3 text-left">PO Group</th>
                <th className="p-3 text-left">Reason for Change</th>
              </tr>
            </thead>
            <tbody>
              {query.isLoading ? (
                <SkeletonRows columns={25} />
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 dark:border-white/5 hover:bg-slate-50 dark:hover:bg-white/5 cursor-pointer" onClick={() => setDetail(r)}>
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onChange={() =>
                          setSelected((prev) => {
                            const next = new Set(prev);
                            next.has(r.id) ? next.delete(r.id) : next.add(r.id);
                            return next;
                          })
                        }
                      />
                    </td>
                    <td className="p-3">{formatDate(r.createdAt, dateFormat)}</td>
                    <td className="p-3">{r.releaseNumber}</td>
                    <td className="p-3">{r.slNumber}</td>
                    <td className="p-3">{r.slaNumber}</td>
                    <td className="p-3">{r.slaLineItem}</td>
                    <td className="p-3">
                      <LineStatusBadge status={r.status} />
                    </td>
                    <td className="p-3">{r.materialNumber}</td>
                    <td className="p-3">{r.materialDescription}</td>
                    <td className="p-3">{formatDate(r.deliveryDate, dateFormat)}</td>
                    <td className="p-3 text-slate-400">—</td>
                    <td className="p-3 text-right">{r.originalQty}</td>
                    <td className="p-3 text-right text-slate-400">—</td>
                    <td className="p-3">{r.unit}</td>
                    <td className="p-3">{plantMap.get(r.plantId) ?? r.plantId}</td>
                    <td className="p-3 text-right">{r.unitPrice?.toFixed(2)}</td>
                    <td className="p-3">{r.currency}</td>
                    <td className="p-3">{vendorMap.get(r.vendorId) ?? r.vendorId}</td>
                    <td className="p-3">{r.paymentTerms}</td>
                    <td className="p-3">{r.mrpControllerNumber}</td>
                    <td className="p-3">{r.mrpControllerName}</td>
                    <td className="p-3">{r.regionalBuyer}</td>
                    <td className="p-3">{r.regionalBuyerName}</td>
                    <td className="p-3">{r.purchaseOrderGroup}</td>
                    <td className="p-3 text-slate-400" title="No change proposed yet">
                      —
                    </td>
                  </tr>
                ))
              )}
              {!query.isLoading && !rows.length && (
                <tr>
                  <td colSpan={25} className="p-8 text-center text-slate-400">
                    No unconfirmed SLA lines match this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="p-4 flex items-center justify-between border-t border-slate-100 dark:border-white/10">
          <span className="text-xs text-slate-400">
            {rows.length} of {total} unconfirmed lines
          </span>
          <div className="flex gap-2">
            <Button variant="outline" icon={Download} onClick={() => api.download("/sla/export/confirmation-result", "SLA_Confirmation_Result.xlsx")}>
              Export to Excel
            </Button>
            <Button variant="ghost" onClick={() => navigate("/")}>
              Back
            </Button>
            <Button variant="outline" icon={Pencil} onClick={changeSelected}>
              Change
            </Button>
            <Button icon={Check} disabled={!selected.size} onClick={() => confirmMutation.mutate([...selected])}>
              Confirm Selected
            </Button>
          </div>
        </div>
      </Card>

      <Drawer open={!!detail} onClose={() => setDetail(null)} title={detail ? `${detail.slaNumber} · Line ${detail.slaLineItem}` : ""}>
        {detail && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[11px] font-semibold text-slate-500">Material</div>
                <div>
                  {detail.materialNumber} — {detail.materialDescription}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold text-slate-500">Delivery Date</div>
                <div>{formatDate(detail.deliveryDate, dateFormat)}</div>
              </div>
              <div>
                <div className="text-[11px] font-semibold text-slate-500">Quantity</div>
                <div>
                  {detail.originalQty} {detail.unit}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold text-slate-500">Unit Price</div>
                <div>
                  {detail.currency} {detail.unitPrice}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold text-slate-500">Vendor</div>
                <div>{vendorMap.get(detail.vendorId) ?? detail.vendorId}</div>
              </div>
              <div>
                <div className="text-[11px] font-semibold text-slate-500">Plant</div>
                <div>{plantMap.get(detail.plantId) ?? detail.plantId}</div>
              </div>
              <div>
                <div className="text-[11px] font-semibold text-slate-500">Purchase Order Group</div>
                <div>{detail.purchaseOrderGroup ?? "—"}</div>
              </div>
              <div>
                <div className="text-[11px] font-semibold text-slate-500">Regional Buyer</div>
                <div>{detail.regionalBuyerName ?? detail.regionalBuyer ?? "—"}</div>
              </div>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-slate-500 mb-1.5">Comments</div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {commentsQuery.data?.comments.length ? (
                  commentsQuery.data.comments.map((c) => (
                    <div key={c.id} className="text-xs bg-slate-50 dark:bg-white/5 rounded-lg p-2">
                      <div>{c.comment}</div>
                      <div className="text-[10px] text-slate-400 mt-1">
                        {c.createdByEmail} · {new Date(c.createdAt).toLocaleString()}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-xs text-slate-400">No comments on this line yet.</div>
                )}
              </div>
            </div>
            <Button
              className="w-full justify-center"
              icon={Check}
              onClick={() => {
                confirmMutation.mutate([detail.id]);
                setDetail(null);
              }}
            >
              Confirm This Line
            </Button>
          </div>
        )}
      </Drawer>
    </div>
  );
}
